import React, { useEffect, useState, useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { FileText, Loader2, X, Save } from "lucide-react";
import { relatoriosAPI } from "../../../services/relatorios";
import { Relatorio } from "../../../types/relatorios";
import { RichTextEditor } from "../../RichTextEditor";

interface FormValues {
  titulo: string;
  categoria: string;
  conteudo: string;
}

interface RelatorioModalProps {
  isOpen: boolean;
  onClose: () => void;
  idGrupo: string;
  editingRelatorio?: Relatorio | null;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  titulo: Yup.string()
    .required("Título é obrigatório")
    .min(3, "Título deve ter pelo menos 3 caracteres")
    .max(255, "Título deve ter no máximo 255 caracteres"),
  categoria: Yup.string()
    .required("Categoria é obrigatória"),
  conteudo: Yup.string()
    .required("Conteúdo é obrigatório")
    .min(10, "Conteúdo deve ter pelo menos 10 caracteres"),
});

const AUTOSAVE_KEY_PREFIX = "relatorio_rascunho_";

export function RelatorioModal({
  isOpen,
  onClose,
  idGrupo,
  editingRelatorio = null,
  onSuccess
}: RelatorioModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    titulo: "",
    categoria: "",
    conteudo: "",
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});
  const [autoSaving, setAutoSaving] = useState(false);

  const autosaveKey = editingRelatorio
    ? `${AUTOSAVE_KEY_PREFIX}${editingRelatorio.id}`
    : `${AUTOSAVE_KEY_PREFIX}novo`;

  // Carregar dados do localStorage ou do relatório em edição
  useEffect(() => {
    if (isOpen) {
      if (editingRelatorio) {
        setFormData({
          titulo: editingRelatorio.titulo,
          categoria: editingRelatorio.categoria || "",
          conteudo: editingRelatorio.conteudo,
        });
        // Limpar autosave se for edição de rascunho
        if (editingRelatorio.status === 'rascunho') {
          const saved = localStorage.getItem(autosaveKey);
          if (saved) {
            try {
              const savedData = JSON.parse(saved);
              setFormData(savedData);
            } catch (e) {
              console.error("Erro ao carregar autosave:", e);
            }
          }
        }
      } else {
        // Tentar recuperar autosave para novo relatório
        const saved = localStorage.getItem(autosaveKey);
        if (saved) {
          try {
            const savedData = JSON.parse(saved);
            setFormData(savedData);
            enqueueSnackbar("Rascunho recuperado automaticamente", { variant: "info" });
          } catch (e) {
            console.error("Erro ao carregar autosave:", e);
            setFormData({
              titulo: "",
              categoria: "",
              conteudo: "",
            });
          }
        } else {
          setFormData({
            titulo: "",
            categoria: "",
            conteudo: "",
          });
        }
      }
      setErrors({});
    }
  }, [editingRelatorio, isOpen]);

  // Auto-save com debounce
  useEffect(() => {
    if (!isOpen) return;

    // Só salvar se houver algum conteúdo
    if (!formData.titulo && !formData.conteudo) return;

    const timeoutId = setTimeout(() => {
      setAutoSaving(true);
      localStorage.setItem(autosaveKey, JSON.stringify(formData));
      setTimeout(() => setAutoSaving(false), 500);
    }, 2000); // 2 segundos de debounce

    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, autosaveKey]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: any = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent, enviar = false) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        id_grupo: idGrupo,
        titulo: formData.titulo,
        categoria: formData.categoria,
        conteudo: formData.conteudo,
      };

      if (editingRelatorio) {
        // Se for edição, primeiro atualizar
        await relatoriosAPI.atualizar(editingRelatorio.id, payload);

        // Se clicar em "Salvar e Enviar", enviar após atualizar
        if (enviar && editingRelatorio.status === 'rascunho') {
          await relatoriosAPI.enviar(editingRelatorio.id);
          enqueueSnackbar("Relatório enviado com sucesso!", { variant: "success" });
        } else {
          enqueueSnackbar("Relatório atualizado com sucesso!", { variant: "success" });
        }
      } else {
        // Criar novo relatório
        const response = await relatoriosAPI.criar(payload);

        // Se clicar em "Salvar e Enviar", enviar após criar
        if (enviar) {
          await relatoriosAPI.enviar(response.id);
          enqueueSnackbar("Relatório criado e enviado com sucesso!", { variant: "success" });
        } else {
          enqueueSnackbar("Rascunho salvo com sucesso!", { variant: "success" });
        }
      }

      // Limpar autosave
      localStorage.removeItem(autosaveKey);

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar relatório", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Perguntar se quer salvar rascunho
    if (formData.titulo || formData.conteudo) {
      const confirm = window.confirm(
        "Você tem alterações não salvas. Elas serão mantidas como rascunho automático. Deseja continuar?"
      );
      if (!confirm) return;
    }

    setFormData({ titulo: "", categoria: "", conteudo: "" });
    setErrors({});
    onClose();
  };

  const handleLimparRascunho = () => {
    if (window.confirm("Deseja realmente limpar o rascunho salvo automaticamente?")) {
      localStorage.removeItem(autosaveKey);
      setFormData({ titulo: "", categoria: "", conteudo: "" });
      enqueueSnackbar("Rascunho limpo!", { variant: "info" });
    }
  };

  if (!isOpen) return null;

  const isRascunho = !editingRelatorio || editingRelatorio.status === 'rascunho';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingRelatorio ? "Editar Relatório" : "Novo Relatório"}
              {autoSaving && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Save className="h-3 w-3" />
                  Salvando automaticamente...
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {editingRelatorio
                ? "Atualize as informações do relatório"
                : "Crie um novo relatório. Rascunhos são salvos automaticamente"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
            <div>
              <Label htmlFor="titulo">Título</Label>
              <InputText
                id="titulo"
                name="titulo"
                title=""
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Título do relatório..."
                error={errors.titulo}
                className="mt-1"
                disabled={loading || !isRascunho}
              />
            </div>

            <div>
              <Label htmlFor="categoria">Categoria</Label>
              <select
                id="categoria"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={loading || !isRascunho}
              >
                <option value="">Selecione uma categoria</option>
                <option value="aula">Aula</option>
                <option value="atividade">Atividade</option>
                <option value="projeto">Projeto</option>
              </select>
              {errors.categoria && (
                <p className="text-sm text-red-500 mt-1">{errors.categoria}</p>
              )}
            </div>

            <div>
              <Label htmlFor="conteudo">Conteúdo</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={formData.conteudo}
                  onChange={(value) => setFormData({ ...formData, conteudo: value })}
                  placeholder="Escreva o conteúdo do relatório..."
                  enableImages
                />
              </div>
              {errors.conteudo && (
                <p className="text-sm text-red-500 mt-1">{errors.conteudo}</p>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              {!editingRelatorio && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleLimparRascunho}
                  disabled={loading}
                  className="text-muted-foreground"
                >
                  Limpar Rascunho
                </Button>
              )}
              <div className="flex-1" />
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              {isRascunho && (
                <>
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="outline"
                    className="border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      "Salvar Rascunho"
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, true)}
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Salvar e Enviar"
                    )}
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
